import type { Plugin } from 'vue';
import { createVuetify, VuetifyOptions } from 'vuetify';
import { VDateInput } from 'vuetify/labs/VDateInput';
import { VuetifyDateAdapter } from 'vuetify/lib/composables/date/adapters/vuetify';

/**
 * Custom date adapter that fixes an off-by-one bug in Vuetify's calendar.js
 * composable for non-Sunday-first locales (e.g., Romanian "ro").
 *
 * The bug: calendar.js `weekdayLabels` indexes into the `getWeekdays()` array
 * using raw JS day-of-week numbers (Sun=0..Sat=6), but `getWeekdays()` returns
 * labels starting from the locale's first day of week (Mon=index 0 for "ro").
 * This causes the weekday headers to be shifted by one position.
 *
 * Fix: Override `getWeekdays()` to always return 7 labels in JS day-of-week
 * order (Sun=0..Sat=6), so the indexing in `weekdayLabels` works correctly.
 */
class FixedDateAdapter extends VuetifyDateAdapter {
    getWeekdays(firstDayOfWeek?: number | string): string[] {
        // Get the original labels (ordered from firstDayOfWeek)
        const originalLabels = super.getWeekdays(firstDayOfWeek);
        
        // Dynamically and 100% accurately determine the first day of week that the
        // date adapter is actually using by checking the start day of a known Sunday.
        const sunday = new Date(2000, 0, 2); // January 2, 2000 was a Sunday
        const start = (this as any).startOfWeek(sunday, firstDayOfWeek);
        const first = start.getDay();

        // Re-arrange: originalLabels[0] corresponds to day `first`,
        // we need to build an array where index = JS day (0=Sun..6=Sat)
        const result: string[] = new Array(7);
        for (let i = 0; i < 7; i++) {
            const jsDay = (first + i) % 7;
            result[jsDay] = originalLabels[i];
        }
        return result;
    }
}

const config: Partial<VuetifyOptions> = {
    components: {
        VDateInput,
    },
    date: {
        adapter: FixedDateAdapter,
    },
    locale: {
        locale: 'en',
        fallback: 'en',
    },
    display: {
        mobileBreakpoint: 'sm',
        thresholds: {
            xs: 0,
            sm: 576,
            md: 768,
            lg: 992,
            xl: 1200,
            xxl: 1400,
        },
    },
    defaults: {
        VCard: {
            flat: true,
        },
        VBtn: {
            flat: true,
        },
        VInput: {
            hideDetails: 'auto',
        },
        VTextField: {
            hideDetails: 'auto',
        },
        VFileInput: {
            hideDetails: 'auto',
        },
        VTextarea: {
            hideDetails: 'auto',
        },
        VCombobox: {
            hideDetails: 'auto',
        },
        VAutocomplete: {
            hideDetails: 'auto',
        },
        VSelect: {
            hideDetails: 'auto',
        },
        VCheckbox: {
            hideDetails: 'auto',
        },
        VSwitch: {
            hideDetails: 'auto',
        },
        VSlider: {
            hideDetails: 'auto',
        },
        VRangeSlider: {
            hideDetails: 'auto',
        },
    },
    theme: {
        themes: {
            light: {
                colors: {
                    primary: '#003366',
                    success: '#27C840',
                },
            },
            dark: {
                colors: {
                    primary: '#ff4433',
                    background: '#0a0a0a',
                    'on-background': '#ededec',
                    surface: '#161615',
                },
            },
        },
    },
};

const plugin: Plugin = {
    install(app) {
        const vuetify = createVuetify(config);
        app.use(vuetify);
    },
};

export default plugin;
